import { useState, useEffect } from "react";
import { Rocket, Trash2, History, ExternalLink, RotateCcw, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { seoPublishedList, seoUnpublish, seoVersions, seoRollback } from "@/lib/api";

export default function ManagedPages() {
  const [pages, setPages] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const [versions, setVersions] = useState([]);
  const [vLoading, setVLoading] = useState(false);

  const load = async () => {
    try { const d = await seoPublishedList(); setPages(d.pages || []); } catch (e) { /* none */ }
  };
  useEffect(() => { load(); }, []);

  const openVersions = async (slug) => {
    if (openSlug === slug) { setOpenSlug(null); return; }
    setOpenSlug(slug); setVersions([]); setVLoading(true);
    try { const d = await seoVersions(slug); setVersions(d.versions || []); } catch (e) { setVersions([]); }
    finally { setVLoading(false); }
  };

  const rollback = async (versionId, slug) => {
    try {
      await seoRollback(versionId);
      toast.success("Rolled back — this version is now live");
      const d = await seoVersions(slug); setVersions(d.versions || []);
      load();
    } catch (e) { toast.error("Rollback failed"); }
  };

  const unpublish = async (slug) => {
    try { await seoUnpublish(slug); toast.success("Unpublished"); if (openSlug === slug) setOpenSlug(null); load(); }
    catch (e) { toast.error("Could not unpublish"); }
  };

  if (pages.length === 0) return null;

  return (
    <div className="mt-14" data-testid="managed-pages">
      <div className="mb-4 flex items-center gap-2">
        <Rocket size={18} className="text-brand" />
        <h2 className="font-heading text-lg font-bold text-slate-900">Published Pages</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Live · versioned</span>
      </div>
      <div className="space-y-3">
        {pages.map((p) => (
          <Card key={p.slug} className="border-slate-200 p-4" data-testid={`pub-card-${p.slug}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{p.title}</div>
                <div className="truncate text-xs text-slate-400">from {p.origin_url}</div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-brand" data-testid={`pub-view-${p.slug}`}>
                  <ExternalLink size={13} /> View
                </a>
                <Button onClick={() => openVersions(p.slug)} size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid={`pub-versions-${p.slug}`}>
                  <History size={13} /> {p.version_count} version{p.version_count === 1 ? "" : "s"}
                </Button>
                <button onClick={() => unpublish(p.slug)} className="text-slate-300 hover:text-rose-500" data-testid={`pub-delete-${p.slug}`}><Trash2 size={15} /></button>
              </div>
            </div>
            {openSlug === p.slug && (
              <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`pub-version-list-${p.slug}`}>
                {vLoading && <div className="flex items-center gap-2 py-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Loading versions…</div>}
                {!vLoading && versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between py-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">v{v.version_no}</span>
                      <span className="text-xs text-slate-400">{v.title}</span>
                      {v.status === "published" && <span className="rounded-full bg-emerald-100 px-2 text-[11px] font-semibold text-emerald-700">LIVE</span>}
                    </div>
                    {v.status !== "published" && (
                      <button onClick={() => rollback(v.id, p.slug)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline" data-testid={`pub-rollback-${v.id}`}>
                        <RotateCcw size={12} /> Roll back to this
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
